/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { SavedOutfit } from '../types';
import { useTranslation } from '../i18n/useTranslation';

interface WardrobeEquityDashboardProps {
    savedOutfits: SavedOutfit[];
}

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const extractPrice = (priceStr?: string) => {
    if (!priceStr) return 0;
    // Extrac digits, keeping decimals
    const match = priceStr.match(/[\d,.]+/);
    if (!match) return 0;
    return parseFloat(match[0].replace(',', '.'));
};

const WardrobeEquityDashboard: React.FC<WardrobeEquityDashboardProps> = ({ savedOutfits }) => {
    const { t } = useTranslation();

    const equityData = useMemo(() => {
        const uniqueItemsMap = new Map<string, any>();
        let totalUsage = 0;

        savedOutfits.forEach(outfit => {
            const allItems = [...(outfit.layer.garments || []), ...(outfit.layer.accessories || [])];
            allItems.forEach(item => {
                if (!item || !item.id) return;
                totalUsage++;
                if (!uniqueItemsMap.has(item.id)) {
                    uniqueItemsMap.set(item.id, {
                        ...item,
                        usageCountInOutfits: 1,
                        estimatedPrice: extractPrice(item.price)
                    });
                } else {
                    const existing = uniqueItemsMap.get(item.id);
                    existing.usageCountInOutfits++;
                }
            });
        });

        const uniqueItems = Array.from(uniqueItemsMap.values());
        const totalValue = uniqueItems.reduce((acc, item) => acc + item.estimatedPrice, 0);
        const versatilityMultiplier = uniqueItems.length > 0 ? (totalUsage / uniqueItems.length).toFixed(2) : '0.00';

        const topInvestmentPieces = [...uniqueItems]
            .filter(i => i.estimatedPrice > 0)
            .sort((a, b) => b.estimatedPrice - a.estimatedPrice)
            .slice(0, 3);

        const mostVersatilePieces = [...uniqueItems]
            .sort((a, b) => b.usageCountInOutfits - a.usageCountInOutfits)
            .slice(0, 3);

        return {
            totalValue,
            versatilityMultiplier,
            topInvestmentPieces,
            mostVersatilePieces,
            uniqueItemsCount: uniqueItems.length
        };
    }, [savedOutfits]);

    return (
        <div className="space-y-8 mt-8">
            <motion.div variants={itemVariants} className="bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 p-8 rounded-2xl shadow-sm">
                <h2 className="text-3xl font-serif text-stone-900 dark:text-stone-100 mb-6 tracking-wide">
                    {t('wardrobe_equity_title', 'Wardrobe Equity')}
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 text-center">
                    <div className="p-6 bg-white dark:bg-stone-800 rounded-xl shadow-sm">
                        <p className="text-sm uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">{t('wardrobe_equity_total_value', 'Total Portfolio Value')}</p>
                        <p className="text-4xl font-serif text-stone-900 dark:text-stone-100">€{equityData.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        <p className="text-xs text-stone-400 mt-2">{t('wardrobe_equity_based_on', 'Based on identified items')}</p>
                    </div>
                    <div className="p-6 bg-white dark:bg-stone-800 rounded-xl shadow-sm">
                        <p className="text-sm uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">{t('wardrobe_equity_versatility', 'Versatility Multiplier')}</p>
                        <p className="text-4xl font-serif text-stone-900 dark:text-stone-100">{equityData.versatilityMultiplier}x</p>
                        <p className="text-xs text-stone-400 mt-2">{t('wardrobe_equity_versatility_desc', 'Average outfits per piece')}</p>
                    </div>
                    <div className="p-6 bg-white dark:bg-stone-800 rounded-xl shadow-sm">
                        <p className="text-sm uppercase tracking-widest text-stone-500 dark:text-stone-400 mb-2">{t('wardrobe_equity_active_pieces', 'Active Assets')}</p>
                        <p className="text-4xl font-serif text-stone-900 dark:text-stone-100">{equityData.uniqueItemsCount}</p>
                        <p className="text-xs text-stone-400 mt-2">{t('wardrobe_equity_active_pieces_desc', 'Unique items in rotation')}</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                        <h3 className="text-xl font-serif text-stone-800 dark:text-stone-200 mb-4 border-b border-stone-200 dark:border-stone-700 pb-2">{t('wardrobe_equity_top_investments', 'Key Investment Pieces')}</h3>
                        <div className="space-y-4">
                            {equityData.topInvestmentPieces.length > 0 ? equityData.topInvestmentPieces.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 bg-white dark:bg-stone-800 p-3 rounded-lg">
                                    <img src={item.url} alt={t(item.nameKey)} className="w-16 h-16 object-cover rounded-md" />
                                    <div className="flex-grow">
                                        <p className="font-semibold text-stone-900 dark:text-white uppercase text-sm tracking-wide">{t(item.nameKey)}</p>
                                        <p className="text-stone-500 text-xs">{item.brand || 'Luxury Piece'}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-serif font-semibold text-stone-900 dark:text-white">€{item.estimatedPrice.toLocaleString()}</p>
                                    </div>
                                </div>
                            )) : (
                                <p className="text-stone-500 italic text-sm">{t('wardrobe_equity_no_prices', 'No pricing data available for current items.')}</p>
                            )}
                        </div>
                    </div>

                    <div>
                        <h3 className="text-xl font-serif text-stone-800 dark:text-stone-200 mb-4 border-b border-stone-200 dark:border-stone-700 pb-2">{t('wardrobe_equity_most_versatile', 'High-Yield Assets (Most Versatile)')}</h3>
                        <div className="space-y-4">
                            {equityData.mostVersatilePieces.length > 0 ? equityData.mostVersatilePieces.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 bg-white dark:bg-stone-800 p-3 rounded-lg">
                                    <img src={item.url} alt={t(item.nameKey)} className="w-16 h-16 object-cover rounded-md" />
                                    <div className="flex-grow">
                                        <p className="font-semibold text-stone-900 dark:text-white uppercase text-sm tracking-wide">{t(item.nameKey)}</p>
                                        <p className="text-stone-500 text-xs">{t('wardrobe_equity_featured_in', 'Featured in')} {item.usageCountInOutfits} {t('wardrobe_equity_outfits', 'outfits')}</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-serif font-semibold text-amber-600 dark:text-amber-400">
                                            {item.estimatedPrice > 0 ? `€${(item.estimatedPrice / item.usageCountInOutfits).toFixed(2)}/wear` : 'High ROI'}
                                        </p>
                                    </div>
                                </div>
                            )) : (
                                <p className="text-stone-500 italic text-sm">{t('wardrobe_equity_no_data', 'Not enough data yet.')}</p>
                            )}
                        </div>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default WardrobeEquityDashboard;
